import { useState } from "react";
import { toast } from "sonner";
import { api, errorMessage, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { PageHeader, Card } from "@/components/common/Primitives";
import { Field, Input } from "@/components/common/Field";

function Switch({ checked, onClick, testId, label }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={onClick} data-testid={testId}
      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0071e3] focus-visible:ring-offset-2 active:scale-95 ${checked ? "bg-primary" : "bg-[#e0e0e0] dark:bg-[#3a3a3c]"}`}>
      <span className={`absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

export default function SettingsPage() {
  const { user, tenant, can, reload } = useAuth();
  const { theme, toggle } = useTheme();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [err, setErr] = useState(null);

  const changePin = async (e) => {
    e.preventDefault(); setErr(null);
    if (!/^\d{4,6}$/.test(next)) return setErr("Invalid PIN — must be 4 to 6 digits");
    try { await api.post("/auth/change-pin", { current_pin: cur, new_pin: next }); toast.success("PIN updated"); setCur(""); setNext(""); } catch (e2) { setErr(errorMessage(e2)); }
  };
  const toggleCommissioning = async () => {
    await api.post("/tenant/commissioning", null, { params: { enabled: !tenant.commissioning } });
    toast.success(tenant.commissioning ? "Reminder dispatch activated" : "Commissioning mode on — dispatch paused"); reload();
  };

  return (
    <div>
      <PageHeader title="Settings" testId="settings-header" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card title="Profile" testId="profile-card">
          <dl className="space-y-2 text-sm">
            <div><dt className="fv-caption">Name</dt><dd className="font-medium">{user.name}</dd></div>
            <div><dt className="fv-caption">Phone</dt><dd className="font-medium">{user.phone}</dd></div>
            <div><dt className="fv-caption">Role</dt><dd className="font-medium">{ROLE_LABELS[user.role]}</dd></div>
            <div><dt className="fv-caption">Tenant</dt><dd className="font-medium">{tenant?.name}</dd></div>
          </dl>
        </Card>
        <Card title="Appearance" testId="appearance-card">
          <div className="flex items-center justify-between text-sm"><span>Dark mode</span>
            <Switch checked={theme === "dark"} onClick={toggle} testId="settings-dark-mode-switch" label="Toggle dark mode" />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Install Insideman's Garage: open your browser menu and choose “Add to Home Screen” / “Install app”.</p>
        </Card>
        <Card title="Change PIN" testId="change-pin-card">
          <form onSubmit={changePin} className="space-y-3" noValidate>
            <Field label="Current PIN" required><Input data-testid="current-pin-input" type="password" inputMode="numeric" maxLength={6} value={cur} onChange={(e) => setCur(e.target.value.replace(/\D/g, ""))} /></Field>
            <Field label="New PIN" required error={err} hint={!err && "4 to 6 digits"}><Input data-testid="new-pin-input" type="password" inputMode="numeric" maxLength={6} value={next} onChange={(e) => setNext(e.target.value.replace(/\D/g, ""))} error={!!err} /></Field>
            <button type="submit" data-testid="change-pin-submit-btn" className="h-10 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]">Update PIN</button>
          </form>
        </Card>
        {can("admin") && (
          <Card title="Commissioning mode" testId="commissioning-card">
            <p className="text-sm text-muted-foreground">While on, reminders are shown in-app but no notifications are dispatched — this prevents alert flooding while historical data is being entered and verified.</p>
            <div className="mt-3 flex items-center justify-between text-sm"><span className="font-medium" data-testid="commissioning-state">{tenant?.commissioning ? "On — dispatch paused" : "Off — dispatch active"}</span>
              <Switch checked={!!tenant?.commissioning} onClick={toggleCommissioning} testId="commissioning-switch" label="Toggle commissioning mode" />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
